import type {
  ArrayAccess,
  BinaryOperator,
  DeclaredType,
  Declaration,
  Expression,
  ForStatement,
  Identifier,
  Program,
  Statement,
  SwitchCaseValue,
} from '../parser/ast.js';
import { Dialect } from '../dialect.js';
import { matchRepeatUntilAsFor, matchWhileAsFor, numericConstant } from '../transform/repetition.js';
import { asLinearShift, assignedNames } from '../transform/selection.js';

/**
 * Ανίχνευση (ΜΟΝΟ στατική, καμία εκτέλεση) σημείων "ΔΙΑΒΑΣΕ σε στοιχείο
 * πίνακα μέσα σε ΓΙΑ" Ή "ΔΙΑΒΑΣΕ σε απλή (μη-πίνακα) μεταβλητή" όπου θα
 * μπορούσε να προσφερθεί αυτόματο γέμισμα με τυχαίες τιμές (κουμπί στο web
 * UI) — βλ. `ArrayFillSuggestion`.
 *
 * "Decline don't guess": αναγνωρίζουμε ΜΟΝΟ πολύ συγκεκριμένα, σαφή σχήματα
 * (όρια ΓΙΑ σταθερά, δείκτες = ακριβώς οι μεταβλητές των περιβαλλόντων ΓΙΑ,
 * συνθήκη εγκυρότητας σε γνωστή μορφή) — οτιδήποτε άλλο απλώς δεν προτείνει
 * κουμπί (`findArrayFillSuggestions` το παραλείπει), δεν προσπαθεί να
 * μαντέψει.
 *
 * ΣΗΜΑΝΤΙΚΟ όριο εμβέλειας: προτείνουμε αυτόματο γέμισμα ΜΟΝΟ όταν ξέρουμε ΜΕ
 * ΒΕΒΑΙΟΤΗΤΑ τον τύπο του στόχου — δηλαδή ΜΟΝΟ σε πλήρες πρόγραμμα ΓΛΩΣΣΑΣ
 * (έχει υποχρεωτικό τμήμα ΜΕΤΑΒΛΗΤΕΣ, βλ. `declaredVariableTypes`). Στην
 * Ψευδογλώσσα (καμία δήλωση τύπου, πουθενά) ΚΑΙ στο "τμήμα αλγορίθμου"
 * (χωρίς ΜΕΤΑΒΛΗΤΕΣ, ανεξαρτήτως διαλέκτου) δεν υπάρχει τρόπος να ξέρουμε αν
 * μια μεταβλητή είναι π.χ. ακέραια ή πραγματική χωρίς να μαντέψουμε — οπότε
 * ΔΕΝ προτείνουμε τίποτα εκεί (βλ. `buildSuggestion`/`buildScalarSuggestion`,
 * που επιστρέφουν `null` αν λείπει δηλωμένος τύπος, χωρίς κανένα fallback).
 */

export type ArrayFillElementType = DeclaredType;

export type ArrayFillValuePattern =
  | {
      readonly kind: 'range';
      readonly min: number | null;
      readonly minInclusive: boolean;
      readonly max: number | null;
      readonly maxInclusive: boolean;
    }
  | { readonly kind: 'set'; readonly values: readonly (number | string | boolean)[] }
  | { readonly kind: 'unconstrained' };

export interface ArrayFillSuggestion {
  /** Όνομα του πίνακα (`targetKind: 'array'`) ή της απλής μεταβλητής (`targetKind: 'scalar'`). */
  readonly name: string;
  readonly targetKind: 'array' | 'scalar';
  /** Πλήθος στοιχείων που θα γεμίσουν — γινόμενο των μεγεθών των περιβαλλόντων ΓΙΑ που αντιστοιχούν στους δείκτες. `null` για `targetKind: 'scalar'` (δεν έχει νόημα "πλήθος στοιχείων" για μία μεταβλητή). */
  readonly totalCount: number | null;
  readonly elementType: ArrayFillElementType;
  readonly pattern: ArrayFillValuePattern;
  /**
   * Γραμμές πηγαίου κώδικα όπου εμφανίζεται το ΔΙΑΒΑΣΕ αυτού του σχήματος —
   * ΜΙΑ γραμμή στο σχήμα ΜΕΧΡΙΣ_ΟΤΟΥ (η ίδια εντολή ξανατρέχει σε κάθε
   * προσπάθεια εγκυρότητας), ΔΥΟ στο σχήμα ΟΣΟ (το αρχικό ΔΙΑΒΑΣΕ + αυτό
   * μέσα στο ΟΣΟ). Χρησιμοποιείται από το web UI για να αναγνωρίζει ΠΟΤΕ
   * ένα αίτημα εισόδου ανήκει ακόμα σε αυτό το γέμισμα πίνακα (βλ.
   * `promptForInput` στο main.ts) — μόλις η τρέχουσα γραμμή εκτέλεσης βγει
   * από αυτό το σύνολο, η αυτόματη λειτουργία γεμίσματος σταματάει μόνη της.
   */
  readonly readLines: readonly number[];
}

/** `ΔΙΑΒΑΣΕ α, β, γ` — βλ. `buildMultiReadSuggestion`. */
export interface MultiReadFillSuggestion {
  /**
   * Οι στόχοι, ΜΕ ΤΗ ΣΕΙΡΑ που εμφανίζονται στο ΔΙΑΒΑΣΕ — ο interpreter τους
   * διαβάζει με αυτή ακριβώς τη σειρά. `targetKind`/`totalCount` όπως στο
   * `ArrayFillSuggestion` — ένας στόχος μπορεί να είναι ΕΙΤΕ απλή μεταβλητή
   * (`totalCount: null`) ΕΙΤΕ στοιχείο πίνακα μέσα σε περιβάλλον(τα) ΓΙΑ
   * (`totalCount` = πλήθος επαναλήψεων), π.χ. "ΔΙΑΒΑΣΕ Α[i], Β[i]" μέσα σε
   * ΓΙΑ i — ρητό αίτημα ("πολλοί πίνακες με τον ίδιο δείκτη μέσα σε ΓΙΑ").
   * Το ΙΔΙΟ αίτημα ΔΙΑΒΑΣΕ ξαναζητά τιμές γι' αυτούς τους στόχους σε ΚΑΘΕ
   * επανάληψη του ΓΙΑ — το web UI (βλ. `activeMultiReadFills`) ήδη κάνει
   * κυκλική εναλλαγή στους στόχους (modulo), οπότε αυτό λειτουργεί χωρίς
   * καμία επιπλέον αλλαγή εκεί.
   */
  readonly targets: readonly {
    readonly name: string;
    readonly targetKind: 'array' | 'scalar';
    readonly totalCount: number | null;
    readonly elementType: ArrayFillElementType;
    readonly pattern: ArrayFillValuePattern;
  }[];
  readonly readLines: readonly number[];
}

interface ForContext {
  readonly variable: string;
  readonly count: number;
}

interface WalkState {
  readonly forChain: readonly ForContext[];
}

function evaluateForTripCount(stmt: ForStatement): number | null {
  const from = numericConstant(stmt.from);
  const to = numericConstant(stmt.to);
  const step = stmt.step === null ? 1 : numericConstant(stmt.step);
  if (from === null || to === null || step === null || step === 0) return null;
  const count = Math.floor((to - from) / step) + 1;
  return count > 0 ? count : 0;
}

/** `ΔΙΑΒΑΣΕ Πίνακας[...]` — ακριβώς ΕΝΑΣ στόχος, που είναι στοιχείο πίνακα. */
function singleArrayTarget(stmt: Statement): ArrayAccess | null {
  if (stmt.kind !== 'ReadStatement') return null;
  if (stmt.targets.length !== 1) return null;
  const target = stmt.targets[0]!;
  return target.kind === 'ArrayAccess' ? target : null;
}

/** `ΔΙΑΒΑΣΕ Χ` — ακριβώς ΕΝΑΣ στόχος, που είναι ΑΠΛΗ (όχι πίνακα) μεταβλητή. */
function singleScalarTarget(stmt: Statement): Identifier | null {
  if (stmt.kind !== 'ReadStatement') return null;
  if (stmt.targets.length !== 1) return null;
  const target = stmt.targets[0]!;
  return target.kind === 'Identifier' ? target : null;
}

function sameArrayAccess(a: ArrayAccess, b: ArrayAccess): boolean {
  if (a.array.name !== b.array.name) return false;
  if (a.indices.length !== b.indices.length) return false;
  return a.indices.every((idx, i) => {
    const other = b.indices[i]!;
    return idx.kind === 'Identifier' && other.kind === 'Identifier' && idx.name === other.name;
  });
}

function sameIdentifier(a: Identifier, b: Identifier): boolean {
  return a.name === b.name;
}

function isMatchingArrayAccess(expr: Expression, arrayName: string, indexNames: readonly string[]): boolean {
  return (
    expr.kind === 'ArrayAccess' &&
    expr.array.name === arrayName &&
    expr.indices.length === indexNames.length &&
    expr.indices.every((idx, i) => idx.kind === 'Identifier' && idx.name === indexNames[i])
  );
}

function isMatchingIdentifier(expr: Expression, name: string): boolean {
  return expr.kind === 'Identifier' && expr.name === name;
}

function literalValue(expr: Expression): number | string | boolean | null {
  if (expr.kind === 'NumberLiteral') return expr.value;
  if (expr.kind === 'StringLiteral') return expr.value;
  if (expr.kind === 'BooleanLiteral') return expr.value;
  const asNumber = numericConstant(expr);
  return asNumber;
}

const NEGATED_COMPARISON: Partial<Record<BinaryOperator, BinaryOperator>> = {
  '=': '<>',
  '<>': '=',
  '<': '>=',
  '<=': '>',
  '>': '<=',
  '>=': '<',
};

/**
 * Λογική άρνηση μιας συνθήκης — χρειάζεται για το σχήμα ΟΣΟ (η συνθήκη εκεί
 * περιγράφει πότε ΣΥΝΕΧΙΖΕΙ, δηλαδή την ΑΡΝΗΣΗ του "έγκυρο"). Αναγνωρίζει
 * ΜΟΝΟ συγκρίσεις/ΚΑΙ/Η/ΟΧΙ — οτιδήποτε άλλο επιστρέφει `null` (decline).
 */
function negateCondition(expr: Expression): Expression | null {
  if (expr.kind === 'UnaryExpression' && expr.operator === 'ΟΧΙ') return expr.operand;
  if (expr.kind === 'BinaryExpression') {
    const flipped = NEGATED_COMPARISON[expr.operator];
    if (flipped !== undefined) {
      return { kind: 'BinaryExpression', operator: flipped, left: expr.left, right: expr.right, line: expr.line, column: expr.column };
    }
    if (expr.operator === 'ΚΑΙ' || expr.operator === 'Η') {
      const negLeft = negateCondition(expr.left);
      const negRight = negateCondition(expr.right);
      if (negLeft === null || negRight === null) return null;
      return {
        kind: 'BinaryExpression',
        operator: expr.operator === 'ΚΑΙ' ? 'Η' : 'ΚΑΙ',
        left: negLeft,
        right: negRight,
        line: expr.line,
        column: expr.column,
      };
    }
  }
  return null;
}

/**
 * Αλυσίδα `Στόχος = literal1 Η Στόχος = literal2 Η ...` — σύνολο διακριτών
 * έγκυρων τιμών. `isTarget` αναγνωρίζει τον στόχο (στοιχείο πίνακα ΜΕ τους
 * συγκεκριμένους δείκτες, ή απλή μεταβλητή — βλ. `isMatchingArrayAccess`/
 * `isMatchingIdentifier`) — ίδιος αλγόριθμος αναγνώρισης και για τα δύο.
 */
function collectEqualitySet(expr: Expression, isTarget: (expr: Expression) => boolean): (number | string | boolean)[] | null {
  if (expr.kind === 'BinaryExpression' && expr.operator === 'Η') {
    const left = collectEqualitySet(expr.left, isTarget);
    const right = collectEqualitySet(expr.right, isTarget);
    if (left === null || right === null) return null;
    return [...left, ...right];
  }
  if (expr.kind === 'BinaryExpression' && expr.operator === '=') {
    const elementSide = isTarget(expr.left) ? expr.right : isTarget(expr.right) ? expr.left : null;
    if (elementSide === null) return null;
    const value = literalValue(elementSide);
    return value === null ? null : [value];
  }
  return null;
}

interface RangeBounds {
  readonly min: number | null;
  readonly minInclusive: boolean;
  readonly max: number | null;
  readonly maxInclusive: boolean;
}

const UNBOUNDED: RangeBounds = { min: null, minInclusive: true, max: null, maxInclusive: true };

/** Ένα μονόπλευρο ή δίπλευρο αριθμητικό όριο πάνω στον στόχο — π.χ. `Χ>0`, ή `Χ>=0 ΚΑΙ Χ<=100`. Διακρίνει `>`/`<` (γνήσιο, όχι-συμπεριλαμβανόμενο) από `>=`/`<=` (συμπεριλαμβανόμενο). Ίδιο `isTarget` με το `collectEqualitySet`. */
function collectRangeBounds(expr: Expression, isTarget: (expr: Expression) => boolean): RangeBounds | null {
  if (expr.kind === 'BinaryExpression' && expr.operator === 'ΚΑΙ') {
    const left = collectRangeBounds(expr.left, isTarget);
    const right = collectRangeBounds(expr.right, isTarget);
    if (left === null || right === null) return null;
    if ((left.min !== null && right.min !== null) || (left.max !== null && right.max !== null)) return null; // δύο κάτω ή δύο πάνω όρια ταυτόχρονα — δεν το αναγνωρίζουμε
    return {
      min: left.min ?? right.min,
      minInclusive: left.min !== null ? left.minInclusive : right.minInclusive,
      max: left.max ?? right.max,
      maxInclusive: left.max !== null ? left.maxInclusive : right.maxInclusive,
    };
  }
  if (expr.kind !== 'BinaryExpression') return null;
  const elementOnLeft = isTarget(expr.left);
  const elementOnRight = isTarget(expr.right);
  if (!elementOnLeft && !elementOnRight) return null;
  const boundExpr = elementOnLeft ? expr.right : expr.left;
  const bound = numericConstant(boundExpr);
  if (bound === null) return null;
  // Κανονικοποίηση κατεύθυνσης: αν το στοιχείο είναι δεξιά (π.χ. "0 <= Χ"), αντιστρέφουμε νοητά τον τελεστή.
  const operator = elementOnLeft ? expr.operator : mirrorOperator(expr.operator);
  switch (operator) {
    case '>':
      return { ...UNBOUNDED, min: bound, minInclusive: false };
    case '>=':
      return { ...UNBOUNDED, min: bound, minInclusive: true };
    case '<':
      return { ...UNBOUNDED, max: bound, maxInclusive: false };
    case '<=':
      return { ...UNBOUNDED, max: bound, maxInclusive: true };
    default:
      return null;
  }
}

function mirrorOperator(operator: BinaryOperator): BinaryOperator {
  switch (operator) {
    case '<':
      return '>';
    case '<=':
      return '>=';
    case '>':
      return '<';
    case '>=':
      return '<=';
    default:
      return operator;
  }
}

function recognizePattern(validCondition: Expression, isTarget: (expr: Expression) => boolean): ArrayFillValuePattern | null {
  const equalitySet = collectEqualitySet(validCondition, isTarget);
  if (equalitySet !== null && equalitySet.length > 0) return { kind: 'set', values: equalitySet };
  const range = collectRangeBounds(validCondition, isTarget);
  if (range !== null && (range.min !== null || range.max !== null)) {
    return { kind: 'range', min: range.min, minInclusive: range.minInclusive, max: range.max, maxInclusive: range.maxInclusive };
  }
  return null;
}

/**
 * `isTarget` ακριβώς αριστερά ή δεξιά από DIV/MOD, ΟΠΟΥΔΗΠΟΤΕ στο σώμα —
 * η ΓΛΩΣΣΑ δέχεται μόνο ΜΗ ΑΡΝΗΤΙΚΟΥΣ τελεστές για DIV/MOD (βλ.
 * `applyDivModConstraint`), οπότε ένα τυχαία γεμισμένο αρνητικό θα έσκαγε με
 * σφάλμα εκτέλεσης στην πρώτη κιόλας χρήση. ΔΕΝ κοιτάμε μόνο τη συνθήκη
 * εγκυρότητας (όπως το `recognizePattern`) — το DIV/MOD μπορεί να εμφανίζεται
 * ΟΠΟΥΔΗΠΟΤΕ αλλού στο πρόγραμμα (π.χ. σε μια απλή ανάθεση).
 */
function exprContainsDivModOperand(expr: Expression, isTarget: (expr: Expression) => boolean): boolean {
  switch (expr.kind) {
    case 'BinaryExpression':
      if ((expr.operator === 'DIV' || expr.operator === 'MOD') && (isTarget(expr.left) || isTarget(expr.right))) return true;
      return exprContainsDivModOperand(expr.left, isTarget) || exprContainsDivModOperand(expr.right, isTarget);
    case 'UnaryExpression':
      return exprContainsDivModOperand(expr.operand, isTarget);
    case 'ArrayAccess':
      return expr.indices.some((idx) => exprContainsDivModOperand(idx, isTarget));
    case 'FunctionCall':
      return expr.args.some((arg) => exprContainsDivModOperand(arg, isTarget));
    default:
      return false;
  }
}

function switchCaseValueContainsDivModOperand(value: SwitchCaseValue, isTarget: (expr: Expression) => boolean): boolean {
  if (value.kind === 'CaseRange') return exprContainsDivModOperand(value.from, isTarget) || exprContainsDivModOperand(value.to, isTarget);
  if (value.kind === 'CaseComparison') return exprContainsDivModOperand(value.value, isTarget);
  return exprContainsDivModOperand(value, isTarget);
}

/** Πλήρης περιήγηση (ΟΛΑ τα είδη εντολών, ΟΛΕΣ οι εκφράσεις τους) — δεν περιορίζεται στα κοντέινερ ελέγχου ροής όπως το `walkStatements` (που ψάχνει ΜΟΝΟ για σχήματα ΔΙΑΒΑΣΕ). */
function statementsContainDivModOperand(statements: readonly Statement[], isTarget: (expr: Expression) => boolean): boolean {
  for (const stmt of statements) {
    switch (stmt.kind) {
      case 'AssignmentStatement':
        if (exprContainsDivModOperand(stmt.value, isTarget)) return true;
        if (stmt.target.kind === 'ArrayAccess' && stmt.target.indices.some((idx) => exprContainsDivModOperand(idx, isTarget))) return true;
        break;
      case 'ReadStatement':
        if (stmt.targets.some((t) => t.kind === 'ArrayAccess' && t.indices.some((idx) => exprContainsDivModOperand(idx, isTarget)))) return true;
        break;
      case 'WriteStatement':
        if (stmt.values.some((v) => exprContainsDivModOperand(v, isTarget))) return true;
        break;
      case 'SwapStatement':
        if ([stmt.left, stmt.right].some((lv) => lv.kind === 'ArrayAccess' && lv.indices.some((idx) => exprContainsDivModOperand(idx, isTarget)))) return true;
        break;
      case 'IfStatement':
        if (exprContainsDivModOperand(stmt.condition, isTarget)) return true;
        if (statementsContainDivModOperand(stmt.thenBranch, isTarget)) return true;
        for (const branch of stmt.elseIfBranches) {
          if (exprContainsDivModOperand(branch.condition, isTarget)) return true;
          if (statementsContainDivModOperand(branch.body, isTarget)) return true;
        }
        if (stmt.elseBranch !== null && statementsContainDivModOperand(stmt.elseBranch, isTarget)) return true;
        break;
      case 'ForStatement':
        if (exprContainsDivModOperand(stmt.from, isTarget) || exprContainsDivModOperand(stmt.to, isTarget)) return true;
        if (stmt.step !== null && exprContainsDivModOperand(stmt.step, isTarget)) return true;
        if (statementsContainDivModOperand(stmt.body, isTarget)) return true;
        break;
      case 'WhileStatement':
        if (exprContainsDivModOperand(stmt.condition, isTarget)) return true;
        if (statementsContainDivModOperand(stmt.body, isTarget)) return true;
        break;
      case 'RepeatUntilStatement':
        if (statementsContainDivModOperand(stmt.body, isTarget)) return true;
        if (exprContainsDivModOperand(stmt.condition, isTarget)) return true;
        break;
      case 'SwitchStatement':
        if (exprContainsDivModOperand(stmt.discriminant, isTarget)) return true;
        for (const switchCase of stmt.cases) {
          if (switchCase.values.some((v) => switchCaseValueContainsDivModOperand(v, isTarget))) return true;
          if (statementsContainDivModOperand(switchCase.body, isTarget)) return true;
        }
        if (stmt.elseBody !== null && statementsContainDivModOperand(stmt.elseBody, isTarget)) return true;
        break;
      case 'CallStatement':
        if (stmt.args.some((a) => exprContainsDivModOperand(a, isTarget))) return true;
        break;
    }
  }
  return false;
}

/**
 * Επιβάλλει "μη αρνητικό" πάνω σε ένα ήδη αναγνωρισμένο μοτίβο, αν ο στόχος
 * χρησιμοποιείται σαν όρισμα DIV/MOD κάπου στο πρόγραμμα — σφίγγει το κάτω
 * όριο σε 0 (συμπεριλαμβανομένου) αν δεν υπάρχει ήδη αυστηρότερο. Τα σαφή
 * σύνολα τιμών (`'set'`, π.χ. από ρητή συνθήκη ισότητας) ΔΕΝ πειράζονται —
 * εκεί ο μαθητής έχει ήδη πει ρητά ποιες τιμές είναι έγκυρες.
 */
function applyDivModConstraint(pattern: ArrayFillValuePattern, usedAsDivModOperand: boolean): ArrayFillValuePattern {
  if (!usedAsDivModOperand) return pattern;
  if (pattern.kind === 'unconstrained') return { kind: 'range', min: 0, minInclusive: true, max: null, maxInclusive: true };
  if (pattern.kind === 'range' && (pattern.min === null || pattern.min < 0)) return { ...pattern, min: 0, minInclusive: true };
  return pattern;
}

function buildSuggestion(
  access: ArrayAccess,
  ctx: WalkState,
  validCondition: Expression | null,
  readLines: readonly number[],
  declaredTypes: ReadonlyMap<string, DeclaredType>,
  wholeBody: readonly Statement[],
): ArrayFillSuggestion | null {
  const indexNames: string[] = [];
  for (const idx of access.indices) {
    if (idx.kind !== 'Identifier') return null;
    indexNames.push(idx.name);
  }
  if (indexNames.length === 0) return null;

  let totalCount = 1;
  for (const name of indexNames) {
    const loop = ctx.forChain.find((f) => f.variable === name);
    if (loop === undefined) return null;
    totalCount *= loop.count;
  }
  if (totalCount <= 0) return null;

  // "Decline don't guess": χωρίς δηλωμένο τύπο δεν προτείνουμε τίποτα — καμία
  // εικασία από το μοτίβο εγκυρότητας (βλ. σχόλιο αρχείου).
  const elementType = declaredTypes.get(access.array.name);
  if (elementType === undefined) return null;

  const isTarget = (expr: Expression): boolean => isMatchingArrayAccess(expr, access.array.name, indexNames);
  const rawPattern: ArrayFillValuePattern = validCondition === null ? { kind: 'unconstrained' } : (recognizePattern(validCondition, isTarget) ?? { kind: 'unconstrained' });
  const pattern = applyDivModConstraint(rawPattern, statementsContainDivModOperand(wholeBody, isTarget));

  return { name: access.array.name, targetKind: 'array', totalCount, elementType, pattern, readLines };
}

/** Ίδιο με `buildSuggestion`, αλλά για απλή (μη-πίνακα) μεταβλητή — χωρίς δείκτες, χωρίς απαίτηση περιβάλλοντος ΓΙΑ, χωρίς έννοια "πλήθους στοιχείων" (`totalCount: null`). */
function buildScalarSuggestion(
  id: Identifier,
  validCondition: Expression | null,
  readLines: readonly number[],
  declaredTypes: ReadonlyMap<string, DeclaredType>,
  wholeBody: readonly Statement[],
): ArrayFillSuggestion | null {
  const elementType = declaredTypes.get(id.name);
  if (elementType === undefined) return null;

  const isTarget = (expr: Expression): boolean => isMatchingIdentifier(expr, id.name);
  const rawPattern: ArrayFillValuePattern = validCondition === null ? { kind: 'unconstrained' } : (recognizePattern(validCondition, isTarget) ?? { kind: 'unconstrained' });
  const pattern = applyDivModConstraint(rawPattern, statementsContainDivModOperand(wholeBody, isTarget));

  return { name: id.name, targetKind: 'scalar', totalCount: null, elementType, pattern, readLines };
}

/**
 * `ΔΙΑΒΑΣΕ α, β, γ` — ΠΑΝΩ ΑΠΟ ΕΝΑΣ στόχος στην ΙΔΙΑ εντολή. Κάθε στόχος
 * μπορεί να είναι ΕΙΤΕ απλή μεταβλητή ΕΙΤΕ στοιχείο πίνακα μέσα σε
 * περιβάλλον(τα) ΓΙΑ (π.χ. "ΔΙΑΒΑΣΕ Α[i], Β[i]" μέσα σε ΓΙΑ i — ρητό αίτημα:
 * "πολλοί πίνακες με τον ίδιο δείκτη μέσα σε ΓΙΑ"), ίδιος έλεγχος δεικτών με
 * το `buildSuggestion` (χωρίς έλεγχο εγκυρότητας — "decline don't guess": αν
 * ΚΑΠΟΙΟΣ στόχος δεν αναγνωρίζεται με κανέναν από τους δύο τρόπους, ΟΛΟΚΛΗΡΗ
 * η πρόταση απορρίπτεται, όχι μόνο αυτός ο στόχος). Απαιτεί δηλωμένο τύπο
 * για ΚΑΘΕΝΑΝ από τους στόχους (βλ. σχόλιο αρχείου) — στην πράξη λειτουργεί
 * ΜΟΝΟ σε πλήρες πρόγραμμα ΓΛΩΣΣΑΣ.
 *
 * Ο interpreter (`executeRead`) ζητάει μία τιμή ΑΝΑ στόχο, με τη σειρά, όλες
 * στην ΙΔΙΑ γραμμή πηγαίου κώδικα — το web UI (`promptForInput`) καταναλώνει
 * τα `targets` έναν-έναν σε κάθε διαδοχική κλήση για αυτή τη γραμμή (βλ.
 * `activeMultiReadFills` στο main.ts). Αν το ΔΙΑΒΑΣΕ είναι μέσα σε ΓΙΑ, η
 * ΙΔΙΑ γραμμή ξαναζητά τιμές σε ΚΑΘΕ επανάληψη — η ήδη υπάρχουσα κυκλική
 * (modulo) εναλλαγή στόχων στο web UI καλύπτει και αυτό, χωρίς αλλαγή εκεί.
 */
function buildMultiReadSuggestion(
  stmt: Statement,
  ctx: WalkState,
  declaredTypes: ReadonlyMap<string, DeclaredType>,
  wholeBody: readonly Statement[],
): MultiReadFillSuggestion | null {
  if (stmt.kind !== 'ReadStatement') return null;
  if (stmt.targets.length <= 1) return null;
  const targets: MultiReadFillSuggestion['targets'][number][] = [];
  for (const target of stmt.targets) {
    if (target.kind === 'Identifier') {
      const elementType = declaredTypes.get(target.name);
      if (elementType === undefined) return null;
      const isTarget = (expr: Expression): boolean => isMatchingIdentifier(expr, target.name);
      const pattern = applyDivModConstraint({ kind: 'unconstrained' }, statementsContainDivModOperand(wholeBody, isTarget));
      targets.push({ name: target.name, targetKind: 'scalar', totalCount: null, elementType, pattern });
      continue;
    }
    // Στοιχείο πίνακα — ίδιος έλεγχος δεικτών/υπολογισμός πλήθους με το
    // `buildSuggestion`, χωρίς έλεγχο εγκυρότητας (βλ. σχόλιο συνάρτησης).
    const indexNames: string[] = [];
    for (const idx of target.indices) {
      if (idx.kind !== 'Identifier') return null;
      indexNames.push(idx.name);
    }
    if (indexNames.length === 0) return null;
    let totalCount = 1;
    for (const name of indexNames) {
      const loop = ctx.forChain.find((f) => f.variable === name);
      if (loop === undefined) return null;
      totalCount *= loop.count;
    }
    if (totalCount <= 0) return null;
    const elementType = declaredTypes.get(target.array.name);
    if (elementType === undefined) return null;
    const isTarget = (expr: Expression): boolean => isMatchingArrayAccess(expr, target.array.name, indexNames);
    const pattern = applyDivModConstraint({ kind: 'unconstrained' }, statementsContainDivModOperand(wholeBody, isTarget));
    targets.push({ name: target.array.name, targetKind: 'array', totalCount, elementType, pattern });
  }
  return { targets, readLines: [stmt.line] };
}

/**
 * Τρίτο σχήμα "χειροκίνητου ΓΙΑ" — ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ όπου ο μετρητής ΑΥΞΑΝΕΤΑΙ
 * ΠΡΩΤΟΣ μέσα στο σώμα (όχι τελευταίος, βλ. `matchRepeatUntilAsFor` στο
 * transform/repetition.ts — άλλο σχήμα) — π.χ. "ι ← 0 / ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ
 * ι ← ι+1 ... ΜΕΧΡΙΣ_ΟΤΟΥ ι>10". Εξίσου κοινό στο σχολικό βιβλίο — μιας που
 * το ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ τρέχει ΠΑΝΤΑ τουλάχιστον 1 φορά, η αρχικοποίηση "μία
 * θέση πριν" + αύξηση ΠΡΩΤΗ είναι ο φυσικός τρόπος να πάρει ο μετρητής τη
 * σωστή πρώτη τιμή.
 *
 * ΠΡΟΣΟΧΗ (λεπτό σημείο, εύκολο να μπερδευτεί): επειδή η αύξηση γίνεται
 * ΠΡΙΝ τη χρήση αλλά ο έλεγχος ΜΕΤΑ τη χρήση, η "τελευταία τιμή που όντως
 * χρησιμοποιήθηκε" ΔΕΝ είναι το ίδιο το γραμμένο όριο (π.χ. το 10 στο
 * "ι>10") — είναι ΕΝΑ βήμα ΠΙΟ ΠΕΡΑ (11), αφού η συνθήκη ελέγχεται ΑΦΟΥ η
 * ήδη-αυξημένη τιμή έχει ήδη χρησιμοποιηθεί στο σώμα, και σταματάει μόνο
 * όταν ΑΥΤΗ ξεπερνάει το όριο (όχι το ίδιο το όριο). Αντίθετα, στο
 * `matchRepeatUntilAsFor` (αύξηση ΤΕΛΕΥΤΑΙΑ) η χρήση γίνεται ΠΡΙΝ την
 * αύξηση, οπότε εκεί η τελευταία χρησιμοποιημένη τιμή ΕΙΝΑΙ το γραμμένο
 * όριο. Αν δεν διορθωθεί εδώ, το "πλήθος στοιχείων" θα βγει ΛΑΝΘΑΣΜΕΝΟ κατά
 * 1 (bug report: ο χρήστης έδειξε ΑΚΡΙΒΩΣ αυτό το σενάριο, όπου το ίδιο του
 * το όριο ήταν εκτός ορίων πίνακα — υπαρκτό πρόβλημα ΣΤΟΝ κώδικά του, αλλά
 * το γέμισμα πρέπει έστω να δηλώνει το ΣΩΣΤΟ πλήθος, όχι ένα λανθασμένα
 * μικρότερο).
 *
 * "Decline don't guess": δεχόμαστε ΜΟΝΟ όταν η αριθμητική ακολουθία
 * προσγειώνεται ΑΚΡΙΒΩΣ πάνω στο γραμμένο όριο (δηλ. (όριο−αρχική) διαιρείται
 * ακριβώς με το βήμα) — αλλιώς δεν είναι βέβαιο ΠΟΙΑ είναι η πρώτη τιμή που
 * ξεπερνάει το όριο, αρνούμαστε.
 *
 * ΔΕΝ ανήκει στο transform/repetition.ts (δεν είναι το "αντίστροφο" καμιάς
 * υπάρχουσας μετατροπής ΓΙΑ↔ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ) — αποκλειστικά για τον σκοπό
 * του γεμίσματος πίνακα εδώ, όπου χρειαζόμαστε ΜΟΝΟ έναν αριθμητικά
 * υπολογίσιμο μετρητή/εύρος, όχι μια "καθαρή" αναδημιουργήσιμη μετατροπή.
 */
function matchIncrementFirstRepeatAsFor(initStmt: Statement, repeatStmt: Statement): ForStatement | null {
  if (initStmt.kind !== 'AssignmentStatement' || initStmt.target.kind !== 'Identifier') return null;
  if (repeatStmt.kind !== 'RepeatUntilStatement') return null;
  const varName = initStmt.target.name;

  const condition = repeatStmt.condition;
  if (condition.kind !== 'BinaryExpression') return null;
  // "=" ΕΠΙΣΗΣ αποδεκτό (όχι μόνο >/<) — είναι μάλιστα το ΑΣΦΑΛΕΣΤΕΡΟ, πιο
  // συνηθισμένο σωστό ιδίωμα εδώ (π.χ. "ΜΕΧΡΙΣ_ΟΤΟΥ ι=10"): επειδή ο έλεγχος
  // γίνεται ΜΕΤΑ την αύξηση-ΠΡΩΤΗ, το ">"/"<" ΞΕΠΕΡΝΑΕΙ το γραμμένο όριο κατά
  // ένα βήμα (βλ. σχόλιο συνάρτησης), ενώ το "=" σταματάει ΑΚΡΙΒΩΣ πάνω του.
  if (condition.operator !== '>' && condition.operator !== '<' && condition.operator !== '=') return null;
  if (condition.left.kind !== 'Identifier' || condition.left.name !== varName) return null;

  if (repeatStmt.body.length === 0) return null;
  const first = repeatStmt.body[0]!;
  if (first.kind !== 'AssignmentStatement' || first.target.kind !== 'Identifier' || first.target.name !== varName) return null;

  const delta = asLinearShift(first.value, varName);
  if (delta === null || delta === 0) return null;
  if (condition.operator === '>' || condition.operator === '<') {
    if (condition.operator !== (delta > 0 ? '>' : '<')) return null;
  }

  // Χρειαζόμαστε ΚΑΙ την αρχική τιμή ΚΑΙ το γραμμένο όριο σαν αριθμούς (όχι
  // απλά "γνωστές εκφράσεις") — για να υπολογίσουμε το πραγματικό "από"
  // (αρχική+βήμα) ΚΑΙ το πραγματικό "μέχρι" (βλ. σχόλιο συνάρτησης).
  const initialNum = numericConstant(initStmt.value);
  const boundNum = numericConstant(condition.right);
  if (initialNum === null || boundNum === null) return null;
  // Η ακολουθία πρέπει να προσγειώνεται ΑΚΡΙΒΩΣ πάνω στο όριο — αλλιώς decline
  // (για "=" θα σήμαινε άπειρο βρόχο στην πράξη, για ">"/"<" δεν θα ξέραμε με
  // βεβαιότητα ΠΟΙΑ είναι η πρώτη τιμή που το ξεπερνάει).
  if ((boundNum - initialNum) % delta !== 0) return null;

  // Με "=" η τελευταία χρησιμοποιημένη τιμή ΕΙΝΑΙ το ίδιο το γραμμένο όριο·
  // με ">"/"<" είναι ΕΝΑ βήμα πέρα από αυτό (βλ. εκτενές σχόλιο συνάρτησης).
  const actualLastUsed = condition.operator === '=' ? boundNum : boundNum + delta;

  const restOfBody = repeatStmt.body.slice(1);
  if (assignedNames(restOfBody).has(varName)) return null;

  return {
    kind: 'ForStatement',
    variable: initStmt.target,
    from: { kind: 'NumberLiteral', value: initialNum + delta, line: repeatStmt.line, column: repeatStmt.column },
    to: { kind: 'NumberLiteral', value: actualLastUsed, line: repeatStmt.line, column: repeatStmt.column },
    step: delta === 1 ? null : { kind: 'NumberLiteral', value: delta, line: repeatStmt.line, column: repeatStmt.column },
    body: restOfBody,
    // Συνθετικός κόμβος — δεν αντιστοιχεί σε ΠΡΑΓΜΑΤΙΚΟ ΤΕΛΟΣ_ΕΠΑΝΑΛΗΨΗΣ στην
    // πηγή (ΔΕΝ εκτελείται ούτε σχεδιάζεται ποτέ, μόνο για στατικό υπολογισμό
    // πλήθους εδώ) — βλ. σχόλιο `ForStatement.endLine`.
    endLine: null,
    line: initStmt.line,
    column: initStmt.column,
  };
}

function walkStatements(
  statements: readonly Statement[],
  ctx: WalkState,
  declaredTypes: ReadonlyMap<string, DeclaredType>,
  results: ArrayFillSuggestion[],
  multiResults: MultiReadFillSuggestion[],
  wholeBody: readonly Statement[],
): void {
  for (let i = 0; i < statements.length; i++) {
    const stmt = statements[i]!;

    if (stmt.kind === 'ReadStatement' && stmt.targets.length > 1) {
      const multi = buildMultiReadSuggestion(stmt, ctx, declaredTypes, wholeBody);
      if (multi !== null) multiResults.push(multi);
      continue;
    }

    // "Χειροκίνητο ΓΙΑ" γραμμένο με ΟΣΟ Ή ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ (αύξηση στο τέλος
    // Ή στην αρχή του σώματος — βλ. `matchWhileAsFor`/`matchRepeatUntilAsFor`
    // στο transform/repetition.ts, ΚΑΙ `matchIncrementFirstRepeatAsFor` εδώ
    // πιο πάνω). Λειτουργικά ΤΑΥΤΟΣΗΜΟ με ΓΙΑ i ΑΠΟ α ΜΕΧΡΙ β — άρα ο δείκτης
    // i μέσα στο σώμα πρέπει να αναγνωρίζεται εξίσου έγκυρος για γέμισμα
    // πίνακα (π.χ. ΔΙΑΒΑΣΕ Α[i]).
    if (stmt.kind === 'AssignmentStatement') {
      const next = statements[i + 1];
      const forStmt =
        next === undefined
          ? null
          : (matchWhileAsFor(stmt, next)?.apply() ?? matchRepeatUntilAsFor(stmt, next)?.apply() ?? matchIncrementFirstRepeatAsFor(stmt, next));
      if (forStmt !== null) {
        const count = evaluateForTripCount(forStmt);
        const nextCtx: WalkState = count === null ? ctx : { forChain: [...ctx.forChain, { variable: forStmt.variable.name, count }] };
        walkStatements(forStmt.body, nextCtx, declaredTypes, results, multiResults, wholeBody);
        i++; // παραλείπει και το ΟΣΟ/ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ — ήδη καταναλώθηκε σαν μέρος του σχήματος
        continue;
      }
    }

    // Σχήμα 1: ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ ΔΙΑΒΑΣΕ Π[...]/Χ ΜΕΧΡΙΣ_ΟΤΟΥ <έγκυρη συνθήκη πάνω στον στόχο>
    if (stmt.kind === 'RepeatUntilStatement' && stmt.body.length === 1) {
      const arrayTarget = singleArrayTarget(stmt.body[0]!);
      if (arrayTarget !== null) {
        const suggestion = buildSuggestion(arrayTarget, ctx, stmt.condition, [stmt.body[0]!.line], declaredTypes, wholeBody);
        if (suggestion !== null) {
          results.push(suggestion);
          continue;
        }
      }
      const scalarTarget = singleScalarTarget(stmt.body[0]!);
      if (scalarTarget !== null) {
        const suggestion = buildScalarSuggestion(scalarTarget, stmt.condition, [stmt.body[0]!.line], declaredTypes, wholeBody);
        if (suggestion !== null) {
          results.push(suggestion);
          continue;
        }
      }
    }

    if (stmt.kind === 'ReadStatement') {
      const target = singleArrayTarget(stmt);
      if (target !== null) {
        // Σχήμα 2: ΔΙΑΒΑΣΕ Π[...] / ΟΣΟ <άκυρη συνθήκη> ΕΠΑΝΑΛΑΒΕ ΔΙΑΒΑΣΕ Π[...] ΤΕΛΟΣ_ΕΠΑΝΑΛΗΨΗΣ
        const next = statements[i + 1];
        if (next !== undefined && next.kind === 'WhileStatement' && next.body.length === 1) {
          const innerTarget = singleArrayTarget(next.body[0]!);
          if (innerTarget !== null && sameArrayAccess(innerTarget, target)) {
            const validCondition = negateCondition(next.condition);
            const suggestion = buildSuggestion(target, ctx, validCondition, [stmt.line, next.body[0]!.line], declaredTypes, wholeBody);
            if (suggestion !== null) {
              results.push(suggestion);
              i++; // παραλείπει και το ΟΣΟ — ήδη καταναλώθηκε σαν μέρος του σχήματος
              continue;
            }
          }
        }
        // Σχήμα 3: απλό ΔΙΑΒΑΣΕ σε στοιχείο πίνακα, χωρίς έλεγχο εγκυρότητας.
        const suggestion = buildSuggestion(target, ctx, null, [stmt.line], declaredTypes, wholeBody);
        if (suggestion !== null) results.push(suggestion);
        continue;
      }

      const scalarTarget = singleScalarTarget(stmt);
      if (scalarTarget !== null) {
        // Σχήμα 2 (μεταβλητή): ΔΙΑΒΑΣΕ Χ / ΟΣΟ <άκυρη συνθήκη> ΕΠΑΝΑΛΑΒΕ ΔΙΑΒΑΣΕ Χ ΤΕΛΟΣ_ΕΠΑΝΑΛΗΨΗΣ
        const next = statements[i + 1];
        if (next !== undefined && next.kind === 'WhileStatement' && next.body.length === 1) {
          const innerTarget = singleScalarTarget(next.body[0]!);
          if (innerTarget !== null && sameIdentifier(innerTarget, scalarTarget)) {
            const validCondition = negateCondition(next.condition);
            const suggestion = buildScalarSuggestion(scalarTarget, validCondition, [stmt.line, next.body[0]!.line], declaredTypes, wholeBody);
            if (suggestion !== null) {
              results.push(suggestion);
              i++;
              continue;
            }
          }
        }
        // Σχήμα 3 (μεταβλητή): απλό ΔΙΑΒΑΣΕ σε μεταβλητή, χωρίς έλεγχο εγκυρότητας.
        const suggestion = buildScalarSuggestion(scalarTarget, null, [stmt.line], declaredTypes, wholeBody);
        if (suggestion !== null) results.push(suggestion);
        continue;
      }
    }

    switch (stmt.kind) {
      case 'ForStatement': {
        const count = evaluateForTripCount(stmt);
        const nextCtx: WalkState = count === null ? ctx : { forChain: [...ctx.forChain, { variable: stmt.variable.name, count }] };
        walkStatements(stmt.body, nextCtx, declaredTypes, results, multiResults, wholeBody);
        break;
      }
      case 'WhileStatement':
      case 'RepeatUntilStatement':
        walkStatements(stmt.body, ctx, declaredTypes, results, multiResults, wholeBody);
        break;
      case 'IfStatement':
        walkStatements(stmt.thenBranch, ctx, declaredTypes, results, multiResults, wholeBody);
        for (const branch of stmt.elseIfBranches) walkStatements(branch.body, ctx, declaredTypes, results, multiResults, wholeBody);
        if (stmt.elseBranch !== null) walkStatements(stmt.elseBranch, ctx, declaredTypes, results, multiResults, wholeBody);
        break;
      case 'SwitchStatement':
        for (const switchCase of stmt.cases) walkStatements(switchCase.body, ctx, declaredTypes, results, multiResults, wholeBody);
        if (stmt.elseBody !== null) walkStatements(stmt.elseBody, ctx, declaredTypes, results, multiResults, wholeBody);
        break;
      default:
        break;
    }
  }
}

/** Δηλωμένος τύπος κάθε μεταβλητής (ΓΛΩΣΣΑ) — ΚΑΙ πίνακες ΚΑΙ απλές μεταβλητές, μιας που πλέον προτείνουμε γέμισμα και για τα δύο (βλ. `buildSuggestion`/`buildScalarSuggestion`). */
function declaredVariableTypes(declarations: readonly Declaration[]): ReadonlyMap<string, DeclaredType> {
  const map = new Map<string, DeclaredType>();
  for (const decl of declarations) {
    if (decl.kind !== 'VariableDeclaration') continue;
    for (const declarator of decl.declarators) {
      map.set(declarator.name, decl.declaredType);
    }
  }
  return map;
}

/**
 * Σαρώνει ΟΛΟ το σώμα ενός προγράμματος (ΟΧΙ τα υποπρογράμματα — βλ.
 * `findArrayFillSuggestionsInStatements` για να καλυφθούν κι αυτά αν
 * χρειαστεί) και επιστρέφει κάθε αναγνωρισμένο σημείο γεμίσματος πίνακα.
 *
 * Χωρίς ΓΛΩΣΣΑ (άρα χωρίς δηλωμένους τύπους) γυρνάει πάντα κενή λίστα — βλ.
 * σχόλιο αρχείου.
 */
export function findArrayFillSuggestions(program: Program): readonly ArrayFillSuggestion[] {
  const declaredTypes = program.dialect === Dialect.GLOSSA ? declaredVariableTypes(program.declarations) : new Map<string, DeclaredType>();
  const results: ArrayFillSuggestion[] = [];
  const multiResults: MultiReadFillSuggestion[] = [];
  walkStatements(program.body, { forChain: [] }, declaredTypes, results, multiResults, program.body);
  return results;
}

/** Ίδιο με `findArrayFillSuggestions`, αλλά για `ΔΙΑΒΑΣΕ α, β, γ` (πάνω από έναν στόχο στην ίδια εντολή) — βλ. `MultiReadFillSuggestion`. */
export function findMultiReadFillSuggestions(program: Program): readonly MultiReadFillSuggestion[] {
  const declaredTypes = program.dialect === Dialect.GLOSSA ? declaredVariableTypes(program.declarations) : new Map<string, DeclaredType>();
  const results: ArrayFillSuggestion[] = [];
  const multiResults: MultiReadFillSuggestion[] = [];
  walkStatements(program.body, { forChain: [] }, declaredTypes, results, multiResults, program.body);
  return multiResults;
}

/**
 * Ίδιο με `findArrayFillSuggestions`, αλλά για γυμνή λίστα εντολών (π.χ.
 * "τμήμα αλγορίθμου") — χωρίς ρητές δηλώσεις ΜΕΤΑΒΛΗΤΕΣ δεν υπάρχει ποτέ
 * δηλωμένος τύπος εκεί, οπότε πάντα γυρνάει κενή λίστα (βλ. σχόλιο αρχείου).
 * Διατηρείται ως πραγματική σάρωση (όχι hard-coded `[]`) ώστε να συνεχίσει
 * να δουλεύει αυτόματα αν κάποτε το "τμήμα αλγορίθμου" αποκτήσει δηλώσεις.
 */
export function findArrayFillSuggestionsInStatements(statements: readonly Statement[]): readonly ArrayFillSuggestion[] {
  const results: ArrayFillSuggestion[] = [];
  const multiResults: MultiReadFillSuggestion[] = [];
  walkStatements(statements, { forChain: [] }, new Map(), results, multiResults, statements);
  return results;
}

/** Ίδιο με `findMultiReadFillSuggestions`, αλλά για γυμνή λίστα εντολών — βλ. `findArrayFillSuggestionsInStatements`. */
export function findMultiReadFillSuggestionsInStatements(statements: readonly Statement[]): readonly MultiReadFillSuggestion[] {
  const results: ArrayFillSuggestion[] = [];
  const multiResults: MultiReadFillSuggestion[] = [];
  walkStatements(statements, { forChain: [] }, new Map(), results, multiResults, statements);
  return multiResults;
}
